/**
 * 360px-wide compact node navigator for the facilitator.
 * Shows Prev/Next buttons, active node highlight, timer display,
 * and response count per node.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  getSessionNodes,
  advanceNode,
  previousNode,
  gotoNode,
  getActiveNodeIndex,
  nodeTitle,
  nodeTimerMinutes,
  type SessionNode,
} from '../lib/api';
import {
  buildJoinUrl,
  shareJoinLinkToChat,
  inviteAllToApp,
  startSharing,
  stopSharing,
  notifyActivityStarted,
  getRosterCount,
  onRosterChange,
} from '../lib/zoomActions';
import { LatencyIndicator } from './LatencyIndicator';

type ActionStatus = 'idle' | 'sending' | 'sent' | 'error';

/* ------------------------------------------------------------------ */
/*  Type config                                                       */
/* ------------------------------------------------------------------ */

const TYPE_CONFIG: Record<
  string,
  { abbr: string; bg: string; border: string; text: string }
> = {
  question: {
    abbr: 'Q',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  poll: {
    abbr: 'P',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
  },
  quiz: {
    abbr: 'Qz',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
  nps: {
    abbr: 'NPS',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
  },
  crosstab: {
    abbr: 'CT',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    text: 'text-pink-700',
  },
  imageactivity: {
    abbr: 'IA',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-700',
  },
  wordcloud: {
    abbr: 'WC',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-700',
  },
  swotanalysis: {
    abbr: 'SA',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
  },
  exam: {
    abbr: 'EX',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
  },
  slide: {
    abbr: 'SL',
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    text: 'text-gray-600',
  },
};

const BADGE_COLORS: Record<string, string> = {
  question: 'bg-blue-500',
  poll: 'bg-green-500',
  quiz: 'bg-purple-500',
  nps: 'bg-orange-500',
  crosstab: 'bg-pink-500',
  imageactivity: 'bg-cyan-500',
  wordcloud: 'bg-teal-500',
  swotanalysis: 'bg-indigo-500',
  exam: 'bg-red-500',
  slide: 'bg-gray-400',
};

/* ------------------------------------------------------------------ */
/*  Timer helper                                                      */
/* ------------------------------------------------------------------ */

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

interface ZoomFacilitatorViewProps {
  sessionId: string;
  activeNodeIndex: number;
  isPolling: boolean;
  onNodeChange: (index: number) => void;
  responseCounts: Record<string, number>;
}

export function ZoomFacilitatorView({
  sessionId,
  activeNodeIndex,
  isPolling,
  onNodeChange,
  responseCounts,
}: ZoomFacilitatorViewProps) {
  const [nodes, setNodes] = useState<SessionNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [chatStatus, setChatStatus] = useState<ActionStatus>('idle');
  const [inviteStatus, setInviteStatus] = useState<ActionStatus>('idle');
  const [sharing, setSharing] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState(false);
  const [timer, setTimer] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeNodeRef = useRef<HTMLDivElement | null>(null);

  const joinUrl = buildJoinUrl(sessionId);

  /* -- Live roster count (host-only SDK API; best-effort) --------- */
  const [rosterCount, setRosterCount] = useState<number | null>(null);
  useEffect(() => {
    let disposed = false;
    getRosterCount()
      .then((n) => !disposed && setRosterCount(n))
      .catch(() => {}); // capability may not be granted yet — degrade silently
    const off = onRosterChange((n) => !disposed && setRosterCount(n));
    return () => {
      disposed = true;
      off();
    };
  }, []);

  /* -- Load nodes ------------------------------------------------ */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSessionNodes(sessionId);
        if (!cancelled) setNodes(data);
      } catch (err) {
        console.error('Failed to load nodes:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  /* -- Auto-scroll to active node -------------------------------- */
  useEffect(() => {
    if (activeNodeRef.current) {
      activeNodeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [activeNodeIndex]);

  /* -- Timer ----------------------------------------------------- */
  const activeNode = nodes[activeNodeIndex];
  const activeDurationSecs = activeNode
    ? (nodeTimerMinutes(activeNode) ?? 0) * 60
    : 0;

  useEffect(() => {
    // Reset timer when active node changes
    if (activeDurationSecs > 0) {
      setTimer(activeDurationSecs);
      setTimerRunning(true);
    } else {
      setTimer(0);
      setTimerRunning(false);
    }
  }, [activeNodeIndex, activeDurationSecs]);

  useEffect(() => {
    if (timerRunning && timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setTimerRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerRunning, timer]);

  /* -- Navigation ------------------------------------------------ */
  // Nudge participants when a new activity goes live (best-effort —
  // capability may be disabled; never block navigation on it).
  const announce = useCallback(
    (newIndex: number) => {
      const node = nodes[newIndex];
      if (node) notifyActivityStarted(nodeTitle(node)).catch(() => {});
    },
    [nodes],
  );

  const handlePrev = useCallback(async () => {
    if (navigating || activeNodeIndex <= 0) return;
    setNavigating(true);
    try {
      const result = await previousNode(sessionId);
      onNodeChange(result.activeNodeIndex);
      announce(result.activeNodeIndex);
    } catch (err) {
      console.error('Failed to go to previous node:', err);
    } finally {
      setNavigating(false);
    }
  }, [sessionId, activeNodeIndex, navigating, onNodeChange, announce]);

  const handleNext = useCallback(async () => {
    if (navigating || activeNodeIndex >= nodes.length - 1) return;
    setNavigating(true);
    try {
      const result = await advanceNode(sessionId);
      onNodeChange(result.activeNodeIndex);
      announce(result.activeNodeIndex);
    } catch (err) {
      console.error('Failed to advance node:', err);
    } finally {
      setNavigating(false);
    }
  }, [sessionId, activeNodeIndex, nodes.length, navigating, onNodeChange, announce]);

  const handleGoto = useCallback(
    async (index: number) => {
      if (navigating || index === activeNodeIndex) return;
      setNavigating(true);
      try {
        const result = await gotoNode(sessionId, index);
        onNodeChange(result.activeNodeIndex);
        announce(result.activeNodeIndex);
      } catch (err) {
        console.error('Failed to go to node:', err);
      } finally {
        setNavigating(false);
      }
    },
    [sessionId, activeNodeIndex, navigating, onNodeChange, announce],
  );

  /* -- Refresh session state ------------------------------------- */
  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const [data, active] = await Promise.all([
        getSessionNodes(sessionId),
        getActiveNodeIndex(sessionId),
      ]);
      setNodes(data);
      onNodeChange(active.activeNodeIndex);
    } catch (err) {
      console.error('Failed to refresh session:', err);
    } finally {
      setRefreshing(false);
    }
  }, [sessionId, refreshing, onNodeChange]);

  /* -- Share join link to meeting chat --------------------------- */
  const handleShareToChat = useCallback(async () => {
    if (chatStatus === 'sending') return;
    setChatStatus('sending');
    try {
      await shareJoinLinkToChat(sessionId);
      setChatStatus('sent');
      setTimeout(() => setChatStatus('idle'), 2500);
    } catch (err) {
      console.error('Failed to send join link to chat:', err);
      setChatStatus('error');
      setTimeout(() => setChatStatus('idle'), 3000);
    }
  }, [sessionId, chatStatus]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard?.writeText(joinUrl).catch(() => {});
  }, [joinUrl]);

  /* -- Invite all participants to open the app ------------------- */
  const handleInviteAll = useCallback(async () => {
    if (inviteStatus === 'sending') return;
    setInviteStatus('sending');
    try {
      await inviteAllToApp();
      setInviteStatus('sent');
      setTimeout(() => setInviteStatus('idle'), 2500);
    } catch (err) {
      console.error('Failed to invite participants:', err);
      setInviteStatus('error');
      setTimeout(() => setInviteStatus('idle'), 3000);
    }
  }, [inviteStatus]);

  /* -- Share the app view with everyone (Collaborate) ------------ */
  const handleToggleShare = useCallback(async () => {
    if (shareBusy) return;
    setShareBusy(true);
    setShareError(false);
    try {
      if (sharing) {
        await stopSharing();
        setSharing(false);
      } else {
        await startSharing();
        setSharing(true);
      }
    } catch (err) {
      console.error('Failed to toggle sharing:', err);
      setShareError(true);
      setTimeout(() => setShareError(false), 3000);
    } finally {
      setShareBusy(false);
    }
  }, [sharing, shareBusy]);

  /* -- Render ---------------------------------------------------- */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-w-[360px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <h2 className="text-sm font-semibold text-gray-900">
          Session Control
        </h2>
        <div className="flex items-center gap-1.5">
          <LatencyIndicator isPolling={isPolling} />
          {rosterCount !== null && activeNode && (
            <span
              className="text-xs font-medium text-primary-600 mr-1"
              title="Responses on the current activity vs people in the meeting"
            >
              {responseCounts[activeNode.id] ?? 0}/{rosterCount}
            </span>
          )}
          <span className="text-xs text-gray-400 mr-1">
            {activeNodeIndex + 1}/{nodes.length}
          </span>

          {/* Invite participants */}
          <button
            onClick={() => setShowInvite((v) => !v)}
            title="Invite participants"
            className={`p-1.5 rounded-md transition-colors ${
              showInvite
                ? 'bg-primary-50 text-primary-600'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh session"
            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600
              disabled:opacity-50 transition-colors"
          >
            <svg
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Share the facilitator view with everyone (Collaborate) — always
          visible so it's reachable without opening the invite panel. */}
      <div className="px-4 py-2 border-b border-gray-100 bg-white">
        <button
          onClick={handleToggleShare}
          disabled={shareBusy}
          className={`w-full px-3 py-2 text-xs font-medium rounded-lg transition-colors
            disabled:opacity-60 ${
              shareError
                ? 'bg-red-50 text-red-600'
                : sharing
                ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          {shareError
            ? 'Share failed'
            : shareBusy
            ? 'Working…'
            : sharing
            ? 'Stop sharing my view'
            : 'Share my view to everyone'}
        </button>
      </div>

      {/* Invite participants panel */}
      {showInvite && (
        <div className="flex flex-col items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="bg-white p-2 rounded-lg border border-gray-200">
            <QRCodeSVG value={joinUrl} size={116} />
          </div>
          <p className="text-[11px] text-gray-500 break-all text-center leading-snug">
            {joinUrl}
          </p>
          <div className="flex gap-2 w-full">
            <button
              onClick={handleCopyLink}
              className="flex-1 px-3 py-2 text-xs font-medium bg-gray-100 text-gray-700
                rounded-lg hover:bg-gray-200 transition-colors"
            >
              Copy link
            </button>
            <button
              onClick={handleShareToChat}
              disabled={chatStatus === 'sending'}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors
                disabled:opacity-60 ${
                  chatStatus === 'error'
                    ? 'bg-red-50 text-red-600'
                    : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
            >
              {chatStatus === 'sent'
                ? 'Sent ✓'
                : chatStatus === 'error'
                ? 'Failed'
                : chatStatus === 'sending'
                ? 'Sending…'
                : 'Send to chat'}
            </button>
          </div>

          {/* Push the app open in every participant's sidebar */}
          <button
            onClick={handleInviteAll}
            disabled={inviteStatus === 'sending'}
            className={`w-full px-3 py-2 text-xs font-medium rounded-lg transition-colors
              disabled:opacity-60 ${
                inviteStatus === 'error'
                  ? 'bg-red-50 text-red-600'
                  : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
              }`}
          >
            {inviteStatus === 'sent'
              ? 'Invited everyone ✓'
              : inviteStatus === 'error'
              ? 'Invite failed'
              : inviteStatus === 'sending'
              ? 'Inviting…'
              : 'Invite everyone to the app'}
          </button>
        </div>
      )}

      {/* Timer bar */}
      {activeDurationSecs > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Timer</span>
            <span
              className={`text-sm font-mono font-semibold ${
                timer <= 10 && timer > 0
                  ? 'text-red-500'
                  : timer === 0
                  ? 'text-gray-400'
                  : 'text-gray-700'
              }`}
            >
              {formatTime(timer)}
            </span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${
                timer <= 10 && timer > 0 ? 'bg-red-500' : 'bg-primary-500'
              }`}
              style={{
                width: `${
                  activeDurationSecs > 0
                    ? (timer / activeDurationSecs) * 100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Node list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
        {nodes.map((node, idx) => {
          const isActive = idx === activeNodeIndex;
          const cfg = TYPE_CONFIG[node.type] || {
            abbr: '?',
            bg: 'bg-gray-50',
            border: 'border-gray-200',
            text: 'text-gray-600',
          };
          const badgeColor = BADGE_COLORS[node.type] || 'bg-gray-400';
          const count = responseCounts[node.id] ?? 0;

          return (
            <div
              key={node.id}
              ref={isActive ? activeNodeRef : undefined}
              onClick={() => handleGoto(idx)}
              className={`
                flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer
                transition-all duration-150
                ${
                  isActive
                    ? `${cfg.bg} ${cfg.border} border-2 shadow-sm`
                    : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                }
                ${navigating ? 'opacity-60 pointer-events-none' : ''}
              `}
            >
              {/* Badge */}
              <span
                className={`${badgeColor} text-white text-[10px] font-bold rounded px-1.5 py-0.5 min-w-[24px] text-center flex-shrink-0`}
              >
                {cfg.abbr}
              </span>

              {/* Title */}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm truncate ${
                    isActive ? `${cfg.text} font-medium` : 'text-gray-700'
                  }`}
                >
                  {nodeTitle(node)}
                </p>
              </div>

              {/* Response count */}
              <span
                className={`text-xs flex-shrink-0 ${
                  isActive ? cfg.text : 'text-gray-400'
                }`}
              >
                {count > 0 ? `${count}` : ''}
              </span>

              {/* Active indicator */}
              {isActive && (
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500" />
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-200 bg-white">
        <button
          onClick={handlePrev}
          disabled={navigating || activeNodeIndex <= 0}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium
            bg-gray-100 text-gray-700 rounded-lg
            hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed
            transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Prev
        </button>

        <button
          onClick={handleNext}
          disabled={navigating || activeNodeIndex >= nodes.length - 1}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium
            bg-primary-500 text-white rounded-lg
            hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed
            transition-colors"
        >
          Next
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
